import { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { getAdminReviews, setReviewHidden } from '../services/adminReviewService';
import './admin-reviews.css';

const { FiEye, FiEyeOff, FiRefreshCw, FiSearch } = FiIcons;

function ReviewFilters({ rating, status, search, onRating, onStatus, onSearch }) {
  return (
    <div className="review-admin-filters">
      <label className="admin-field-label">
        Search
        <span className="review-admin-search">
          <SafeIcon icon={FiSearch} />
          <input
            type="search"
            value={search}
            onChange={(event) => onSearch(event.target.value)}
            placeholder="Customer, product, order, or review text"
          />
        </span>
      </label>
      <label className="admin-field-label">
        Rating
        <select value={rating} onChange={(event) => onRating(event.target.value)}>
          <option value="">All ratings</option>
          {[5, 4, 3, 2, 1].map((value) => (
            <option key={value} value={value}>{value} stars</option>
          ))}
        </select>
      </label>
      <label className="admin-field-label">
        Visibility
        <select value={status} onChange={(event) => onStatus(event.target.value)}>
          <option value="all">All reviews</option>
          <option value="visible">Visible</option>
          <option value="hidden">Removed from storefront</option>
        </select>
      </label>
    </div>
  );
}

function ReviewRow({ review, busy, onToggle }) {
  return (
    <article className={`review-admin-card ${review.is_hidden ? 'is-hidden' : ''}`}>
      <div className="review-admin-card-main">
        {review.image_url && (
          <img src={review.image_url} alt="" className="review-admin-image" loading="lazy" />
        )}
        <div className="review-admin-copy">
          <div className="review-admin-heading">
            <h2>{review.product_name || 'Product review'}</h2>
            <span className={`review-admin-visibility ${review.is_hidden ? 'hidden' : ''}`}>
              {review.is_hidden ? 'Removed from storefront' : 'Visible'}
            </span>
          </div>
          <div className="review-admin-meta">
            <strong className="review-admin-stars" aria-label={`${review.rating} out of 5 stars`}>
              {'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}
            </strong>
            <span>{review.customer_name}</span>
            <span>{review.order_number || 'Order'}</span>
            <time dateTime={review.created_at}>
              {new Date(review.created_at).toLocaleDateString()}
            </time>
          </div>
          {review.comment && <p className="review-admin-comment">{review.comment}</p>}
        </div>
      </div>
      <button
        type="button"
        className={`admin-btn-${review.is_hidden ? 'secondary' : 'danger'} review-admin-action`}
        onClick={() => onToggle(review)}
        disabled={busy}
      >
        <SafeIcon icon={review.is_hidden ? FiEye : FiEyeOff} />
        {busy ? 'Saving…' : review.is_hidden ? 'Restore review' : 'Remove review'}
      </button>
    </article>
  );
}

function AdminReviewManagement() {
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState('');
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const timer = window.setTimeout(() => {
      setLoading(true);
      setError('');
      getAdminReviews({ rating, status, search })
        .then((rows) => {
          if (active) setReviews(rows);
        })
        .catch((loadError) => {
          if (active) setError(loadError.message);
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 180);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [rating, status, search]);

  async function toggleVisibility(review) {
    setBusyId(review.id);
    setMessage('');
    setError('');
    try {
      await setReviewHidden(review.id, !review.is_hidden);
      setMessage(review.is_hidden
        ? 'Review restored to the storefront.'
        : 'Review removed from the storefront. Its record is retained.');
      setReviews((current) => current.map((item) => (
        item.id === review.id ? { ...item, is_hidden: !item.is_hidden } : item
      )));
    } catch (actionError) {
      setError(actionError.message);
    } finally {
      setBusyId('');
    }
  }

  return (
    <div className="admin-overview-container">
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Review Management</h1>
          <p className="admin-page-subtitle">
            Search customer reviews, filter by rating, and manage storefront visibility.
          </p>
        </div>
        <button
          type="button"
          className="admin-btn-secondary"
          onClick={() => {
            setSearch((current) => `${current}`);
            setMessage('Reviews are up to date.');
          }}
        >
          <SafeIcon icon={FiRefreshCw} />
          Refresh
        </button>
      </div>
      <ReviewFilters
        rating={rating}
        status={status}
        search={search}
        onRating={setRating}
        onStatus={setStatus}
        onSearch={setSearch}
      />
      {message && <div className="admin-alert success">{message}</div>}
      {error && <div className="admin-alert error">{error}</div>}
      {loading ? (
        <div className="admin-loading-state">Loading reviews…</div>
      ) : reviews.length === 0 ? (
        <div className="admin-empty-state">No reviews match these filters.</div>
      ) : (
        <div className="review-admin-list">
          {reviews.map((review) => (
            <ReviewRow
              key={review.id}
              review={review}
              busy={busyId === review.id}
              onToggle={toggleVisibility}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminReviewManagement;